import { useCallback, useEffect, useState } from "react";
import { Braces, Pencil, CalendarClock, Loader2, Smile, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { generateOrthoPdf } from "@/lib/orthoPdf";
import { APPLIANCE_LABEL, PHASE_LABEL, CASE_STATUS, ORTHO_PHASES, fmtDate, monthsBetween } from "@/lib/ortho";
import OrthoCaseForm from "@/components/ortho/OrthoCaseForm";
import OrthoControls from "@/components/ortho/OrthoControls";
import OrthoInstallments from "@/components/ortho/OrthoInstallments";

function Stat({ label, value, sub, testId }) {
  return (
    <div className="card-flat p-4" data-testid={testId}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display font-bold text-lg mt-1 leading-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function OrthoTab({ patientId, patient }) {
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { branding } = useBranding();

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      let doctor = null;
      if (data.case.doctor_id) {
        const users = (await api.get("/users", { params: { role: "odontologo" } })).data;
        doctor = users.find((u) => u.id === data.case.doctor_id) || null;
      }
      generateOrthoPdf({ patient, caseData: data.case, controls: data.controls, installments: data.installments, branding, doctor });
      toast.success("PDF de ortodoncia generado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally { setPdfLoading(false); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/patients/${patientId}/ortho`);
    setData(data);
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const c = data.case;
  if (!c) {
    return (
      <div className="card-flat p-10 text-center space-y-4" data-testid="ortho-empty">
        <Smile className="w-10 h-10 mx-auto text-primary" />
        <div>
          <h3 className="font-display font-bold text-2xl tracking-tight">Sin caso de ortodoncia</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Inicia el caso para registrar aparatología, fase, controles mensuales con línea de tiempo y el plan de cuotas.</p>
        </div>
        <button onClick={() => setFormOpen(true)} className="btn-primary" data-testid="ortho-start-btn">Iniciar caso de ortodoncia</button>
        <OrthoCaseForm open={formOpen} onOpenChange={setFormOpen} patientId={patientId} onSaved={load} />
      </div>
    );
  }

  const elapsed = monthsBetween(c.start_date);
  const pct = c.estimated_months ? Math.min(100, Math.round((elapsed / c.estimated_months) * 100)) : 0;
  const phaseIdx = ORTHO_PHASES.findIndex((p) => p.key === c.phase);
  const nextOverdue = c.next_visit && c.next_visit < new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-8" data-testid="ortho-tab">
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Braces className="w-3 h-3" /> Caso de ortodoncia · <span className={c.status === "activo" ? "text-primary" : ""}>{CASE_STATUS[c.status]}</span></div>
            <h3 className="font-display font-bold text-2xl tracking-tight">{APPLIANCE_LABEL[c.appliance_type] || c.appliance_type}</h3>
            <div className="text-xs text-muted-foreground">Inicio {fmtDate(c.start_date)}{c.doctor_name ? ` · ${c.doctor_name}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} disabled={pdfLoading} className="h-10 px-3 rounded-sm border border-primary text-primary text-sm hover:bg-primary/10 flex items-center gap-2 disabled:opacity-60" data-testid="ortho-pdf-btn">
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Reporte PDF
            </button>
            <button onClick={() => setFormOpen(true)} className="h-10 px-3 rounded-sm border border-border text-sm hover:bg-muted flex items-center gap-2" data-testid="ortho-edit-btn"><Pencil className="w-4 h-4" /> Editar caso</button>
          </div>
        </div>

        <div className="card-flat p-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="label-eyebrow">Avance estimado</span>
            <span className="font-mono" data-testid="ortho-progress-text">Mes {elapsed} de {c.estimated_months} · {pct}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {ORTHO_PHASES.map((p, i) => (
              <span key={p.key} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm border ${i < phaseIdx ? "bg-primary/10 text-primary border-primary/20" : i === phaseIdx ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{p.label}</span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Arco superior" value={c.upper_arch || "—"} testId="ortho-stat-upper" />
          <Stat label="Arco inferior" value={c.lower_arch || "—"} testId="ortho-stat-lower" />
          <Stat label="Elásticos" value={c.elastics || "—"} />
          <Stat label="Próximo control" value={c.next_visit ? fmtDate(c.next_visit) : "Sin programar"} sub={nextOverdue ? "Control atrasado" : c.last_control_date ? `Último: ${fmtDate(c.last_control_date)}` : null} testId="ortho-stat-next" />
        </div>
        {(c.diagnosis || c.objectives) && (
          <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {c.diagnosis && <div><div className="label-eyebrow mb-1">Diagnóstico</div><p className="whitespace-pre-wrap">{c.diagnosis}</p></div>}
            {c.objectives && <div><div className="label-eyebrow mb-1">Objetivos</div><p className="whitespace-pre-wrap">{c.objectives}</p></div>}
          </div>
        )}
        {nextOverdue && (
          <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm p-3" data-testid="ortho-next-overdue">
            <CalendarClock className="w-4 h-4" /> El paciente tiene el control atrasado desde {fmtDate(c.next_visit)}.
          </div>
        )}
      </section>

      <OrthoControls caseData={c} controls={data.controls} onChange={load} />
      <OrthoInstallments caseData={c} installments={data.installments} onChange={load} />
      <OrthoCaseForm open={formOpen} onOpenChange={setFormOpen} patientId={patientId} caseData={c} onSaved={load} />
    </div>
  );
}
